import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  OwcIconButton,
  OwcTypography,
  OwcProgressSpinner,
} from "@one/react";
import { MAX_COUNT_CAPACITY, WHOM_TAB_LABEL } from "../../../../constants";
import { GridAutoCompleteStyles } from "../../GridAutoCompleteStyles";
import styles from "../../../../features/book-equipment/whom/ForWhomMainPage.module.scss";

const renderTags = (selectedObjects) => {
  const renderTagValue = (selectedObjects || []).map((s) => {
    const chipLabel = s?.value ?? s;
    return selectedObjects.length === 1 ? `${chipLabel}` : `${chipLabel}, `;
  });
  if (renderTagValue.length === 0) return null;
  let maxCapacity = MAX_COUNT_CAPACITY - (renderTagValue[0]?.length || 0);
  let remainingCapacity = maxCapacity - 7;
  if (remainingCapacity < 0) remainingCapacity = 0;
  return (
    <>
      <OwcTypography className={styles.renderTextField}>
        {renderTagValue[0]}
      </OwcTypography>
      {renderTagValue.length > 1 && (
        <OwcTypography style={{ marginLeft: "5px" }}>
          {renderTagValue[1].length > remainingCapacity
            ? `${renderTagValue[1].substring(0, remainingCapacity)}...`
            : renderTagValue[1]}
        </OwcTypography>
      )}
      {renderTagValue.length > 2 && (
        <span style={{ color: "#0066CC", marginLeft: "5px" }}>
          +{renderTagValue.length - 2}
        </span>
      )}
    </>
  );
};

const InformationFromInlineEdit = ({
  options,
  selected,
  fixedOption,
  allowEdit = true,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [committed, setCommitted] = useState(null);

  const effectiveSelected = useMemo(
    () => committed ?? selected ?? [],
    [committed, selected],
  );

  const buildValue = (arr) => {
    const fixedArr = fixedOption ? [fixedOption] : [];
    const rest = (arr || []).filter(
      (a) => a && (!fixedOption || a.userId !== fixedOption.userId),
    );
    return [...fixedArr, ...rest];
  };

  const [draft, setDraft] = useState(buildValue(effectiveSelected));

  useEffect(() => {
    if (!isEditing) {
      setDraft(buildValue(effectiveSelected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSelected, isEditing, fixedOption]);

  const handleCancel = () => {
    setDraft(buildValue(effectiveSelected));
    setIsEditing(false);
  };

  const baseline = useMemo(
    () => buildValue(effectiveSelected),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveSelected, fixedOption],
  );

  const hasChanges = useMemo(() => {
    const toSortedIds = (arr) =>
      (arr || []).map((u) => u?.userId).filter(Boolean).sort();
    const draftIds = toSortedIds(draft);
    const baseIds = toSortedIds(baseline);
    if (draftIds.length !== baseIds.length) return true;
    return draftIds.some((id, i) => id !== baseIds[i]);
  }, [draft, baseline]);

  const handleSave = async () => {
    if (saving || !hasChanges) return;
    setSaving(true);
    try {
      const ok = await onSave(draft);
      if (ok !== false) {
        setCommitted(draft);
        setIsEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const displayLabels = (effectiveSelected || [])
    .map((u) => u?.value)
    .filter(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <OwcTypography variant="caption">{WHOM_TAB_LABEL.INFORMATION_FROM}</OwcTypography>
        {allowEdit && !isEditing && (
          <OwcIconButton
            icon="edit"
            type="outlined"
            flat
            onClick={() => setIsEditing(true)}
            style={{ color: "#0B41CD" }}
            aria-label={`Edit ${WHOM_TAB_LABEL.INFORMATION_FROM}`}
          />
        )}
      </div>

      {!isEditing && (
        <OwcTypography variant="subtitle2">
          {displayLabels.length === 0 ? "-" : displayLabels.join(", ")}
        </OwcTypography>
      )}

      {isEditing && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <Autocomplete
            multiple
            data-testid="information-from-quick-edit"
            value={draft}
            limitTags={2}
            disableCloseOnSelect
            onChange={(_event, newValue) => {
              newValue = Array.isArray(newValue) ? newValue : [newValue];
              setDraft(buildValue(newValue));
            }}
            name="bookingFor"
            options={options || []}
            fullWidth={false}
            getOptionLabel={(option) => {
              if (Array.isArray(option) && option.length > 0) {
                return option[0]?.value;
              }
              return option?.value || "";
            }}
            renderTags={renderTags}
            isOptionEqualToValue={(option, val) =>
              (val?.userId && option?.userId) && val.userId === option.userId
            }
            getOptionDisabled={(option) =>
              !!(option && fixedOption && option.value === fixedOption.value)
            }
            selectOnFocus
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                label={WHOM_TAB_LABEL.INFORMATION_FROM}
                variant="filled"
              />
            )}
            componentsProps={{ popper: { sx: { zIndex: 99999 } } }}
            sx={{
              ...(GridAutoCompleteStyles?.gridAutoCompleteLableShrink || {}),
              flex: 1,
              width: "100%",
            }}
          />
          <div style={{ display: "flex", gap: "4px", paddingTop: "8px" }}>
            {saving ? (
              <OwcProgressSpinner diameter={24} />
            ) : (
              <>
                <OwcIconButton
                  icon="circle_confirm"
                  type="filled"
                  flat
                  onClick={handleSave}
                  disabled={!hasChanges}
                  style={{ color: hasChanges ? "#28A745" : undefined }}
                  aria-label="Save"
                />
                <OwcIconButton
                  icon="circle_clear"
                  type="filled"
                  flat
                  onClick={handleCancel}
                  style={{ color: "#DC3545" }}
                  aria-label="Cancel"
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

InformationFromInlineEdit.propTypes = {
  options: PropTypes.array.isRequired,
  selected: PropTypes.array,
  fixedOption: PropTypes.object,
  allowEdit: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

export default InformationFromInlineEdit;
